import React, { useEffect, useState } from 'react'
import {Star, Search, Filter, ArrowRight, Globe, Twitter, BarChart2, MessageCircleDashed as MessageCircle} from 'lucide-react'
import { useProjects, Project } from '../hooks/useProjects'

interface ExplorePageProps {
  onProjectSelect?: (project: Project) => void
}

export function ExplorePage({ onProjectSelect }: ExplorePageProps = {}) {
  const { projects, loading, fetchProjects, incrementClick } = useProjects()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'featured'>('all')
  
  useEffect(() => {
    // Force refresh to bypass cache and show new projects
    fetchProjects({ isActive: true, subscriptionStatus: 'active' }, true)
  }, [])
  
  const featuredProjects = projects.filter(p => p.isFeatured)
  const displayProjects = filterType === 'featured' 
    ? featuredProjects 
    : projects.filter(p => 
        p.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
  
  const handleProjectClick = async (project: Project) => {
    if (onProjectSelect) {
      // Navigate to project profile page
      onProjectSelect(project)
    } else {
      // Fallback: open website directly
      await incrementClick(project._id)
      window.open(project.websiteUrl || project.socialLinks?.website, '_blank')
    }
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] flex items-center justify-center">
        <div className="text-white text-xl">Loading projects...</div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">Explore Projects</h1>
          <p className="text-xl text-gray-400">Discover amazing NFT and crypto projects on Base</p>
        </div>
        
        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search projects..."
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                filterType === 'all'
                  ? 'bg-gradient-to-r from-[#0052FF] to-[#00D9FF] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              All Projects
            </button>
            <button
              onClick={() => setFilterType('featured')}
              className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                filterType === 'featured'
                  ? 'bg-gradient-to-r from-[#0052FF] to-[#00D9FF] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Star className="w-4 h-4" />
              Featured
            </button>
          </div>
        </div>
        
        {/* Featured Section (when showing all) */}
        {filterType === 'all' && featuredProjects.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-6 h-6 text-[#FFD700]" />
              <h2 className="text-3xl font-bold text-white">Featured Projects</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featuredProjects.map((project) => (
                <ProjectCard key={project._id} project={project} onProjectClick={handleProjectClick} featured />
              ))}
            </div>
          </div>
        )}
        
        {/* All Projects Grid */}
        {filterType === 'all' && (
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-white mb-6">
              All Projects
              <span className="text-gray-400 text-xl ml-3">({displayProjects.filter(p => !p.isFeatured).length})</span>
            </h2>
          </div>
        )}
        
        {displayProjects.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
              <Search className="w-10 h-10 text-gray-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">No projects found</h3>
            <p className="text-gray-400">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayProjects.filter(p => filterType === 'all' ? !p.isFeatured : true).map((project) => (
              <ProjectCard key={project._id} project={project} onProjectClick={handleProjectClick} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface ProjectCardProps {
  project: Project
  onProjectClick: (project: Project) => void
  featured?: boolean
}

function ProjectCard({ project, onProjectClick, featured }: ProjectCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  
  return (
    <div
      className={`bg-white/5 border ${featured ? 'border-[#FFD700]/30' : 'border-white/10'} rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#0052FF] hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] cursor-pointer`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onProjectClick(project)}
    >
      {/* Banner */}
      <div className="relative h-48 overflow-hidden">
        {project.bannerImageUrl ? (
          <img
            src={project.bannerImageUrl}
            alt={project.projectName}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0052FF] to-[#00D9FF] flex items-center justify-center">
            <span className="text-6xl font-bold text-white/20">{project.projectName.charAt(0)}</span>
          </div>
        )}
        {featured && (
          <div className="absolute top-3 left-3">
            <span className="flex items-center gap-1 px-3 py-1 bg-[#FFD700]/90 backdrop-blur-sm text-black rounded-full text-sm font-bold">
              <Star className="w-4 h-4" />
              Featured
            </span>
          </div>
        )}
        {project.isDemo && (
          <div className="absolute top-3 right-3">
            <span className="px-3 py-1 bg-purple-600/90 backdrop-blur-sm text-white rounded-full text-sm font-bold">
              DEMO
            </span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-6">
        <div className="flex items-start gap-4 mb-4">
          {/* Profile Image */}
          {project.profileImageUrl ? (
            <img
              src={project.profileImageUrl}
              alt={project.projectName}
              className="w-16 h-16 rounded-full object-cover border-2 border-white/10"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0052FF] to-[#00D9FF] flex items-center justify-center">
              <span className="text-2xl font-bold text-white">{project.projectName.charAt(0)}</span>
            </div>
          )}
          
          {/* Project Info */}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-1">{project.projectName}</h3>
            <p className="text-gray-400 text-sm line-clamp-2">
              {project.description || 'No description available'}
            </p>
          </div>
        </div>
        
        {/* Social Links */}
        {project.socialLinks && (
          <div className="flex items-center gap-3 mb-4">
            {project.socialLinks.twitter && (
              <a
                href={project.socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-400 hover:text-[#00D9FF] transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
            )}
            {(project.socialLinks.website || project.websiteUrl) && (
              <a
                href={project.socialLinks.website || project.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-400 hover:text-[#00D9FF] transition-colors"
              >
                <Globe className="w-5 h-5" />
              </a>
            )}
            {project.socialLinks.chart && (
              <a
                href={project.socialLinks.chart}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-400 hover:text-[#00D9FF] transition-colors"
              >
                <BarChart2 className="w-5 h-5" />
              </a>
            )}
            {project.socialLinks.farcaster && (
              <a
                href={project.socialLinks.farcaster}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-400 hover:text-[#00D9FF] transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            )}
          </div>
        )}
        
        {/* View Button */}
        <div
          className={`flex items-center justify-center gap-2 px-4 py-2 bg-white/10 rounded-lg transition-all duration-300 ${
            isHovered ? 'bg-gradient-to-r from-[#0052FF] to-[#00D9FF]' : ''
          }`}
        >
          <span className="text-white font-medium">View Project</span>
          <ArrowRight className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  )
}
